<script setup>
import { ref, reactive, watch, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import SolicitacaoAdiantamentoModal from '@/components/Financeiro/contasPagarReceber/solicitacaoAdiantamento/SolicitacaoAdiantamentoModal.vue'
import { FIN_ST_SOL_ADT } from '@/constants/FIN_ST_SOL_ADT'

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const props = defineProps({
  idEmbarque: { type: Number, default: null },
  idPurchaseOrder: { type: Number, default: null },
  idOrdemCompra: { type: Number, default: null },
  podeEditar: { type: Boolean, default: false },
  podeVisualizar: { type: Boolean, default: true },
  listas: { type: Object, default: () => ({}) },
  idOrganizacao: { type: Number, default: null }
})

const emit = defineEmits([
  'show-notification',
  'acao-completa'
])

// ============================================================
// ESTADO
// ============================================================
const solicitacoes = ref([])
const isLoading = ref(false)
const sortField = ref('')
const sortOrder = ref('')

const listasInternas = reactive({})

// ============================================================
// COLUNAS DO GRID
// ============================================================
const formatarValor = (valor) => {
  if (valor == null) return '-'
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
}

const formatarData = (data) => {
  if (!data) return '-'
  const [ano, mes, dia] = data.split('-')
  return `${dia}/${mes}/${ano}`
}

const colunas = [
  { field: 'idSolicitacaoAdiantamento', label: 'ID', width: '5%', sortable: true },
  { field: 'nomeParceiro', label: 'Fornecedor', sortable: true, width: '44%' },
  { field: 'valorTotal', label: 'Valor Total', align: 'right', sortable: true, width: '16%', formatter: formatarValor },
  { field: 'nomeMoeda', label: 'Moeda', align: 'center', sortable: true, width: '5%' },
  { field: 'dataVencimento', label: 'Vencimento', align: 'center', sortable: true, width: '10%', formatter: formatarData },
  { field: 'nomeStatusSolicitacao', label: 'Status', align: 'center', sortable: true, width: '12%', slotName: 'status' },
  { field: 'acoes', label: 'Ações', width: '8%', align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

// ============================================================
// CARREGAR LISTAS INTERNAS
// ============================================================
const carregarListasInternas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['SolicitacaoAdiantamento'])
    Object.assign(listasInternas, result)
  } catch (e) {
    console.error('[SolicitacaoAdiantamentoGrid] erro ao carregar listas internas:', e)
  }
}

onMounted(() => {
  carregarListasInternas()
})

// ============================================================
// CARREGAR SOLICITAÇÕES
// ============================================================
const carregarSolicitacoes = async () => {
  const params = { pageSize: 999 }

  if (props.idEmbarque) {
    params.idEmbarque = props.idEmbarque
  } else if (props.idPurchaseOrder) {
    params.idPurchaseOrder = props.idPurchaseOrder
  } else if (props.idOrdemCompra) {
    params.idOrdemCompra = props.idOrdemCompra
  } else {
    solicitacoes.value = []
    return
  }

  if (sortField.value) params.sortField = sortField.value
  if (sortOrder.value) params.sortOrder = sortOrder.value

  isLoading.value = true
  try {
    const res = await api.get('/SolicitacoesAdiantamento', { params })
    solicitacoes.value = res.data
  } catch (e) {
    emit('show-notification', { message: 'Erro ao carregar solicitações de adiantamento.', type: 'error' })
  } finally {
    isLoading.value = false
  }
}

// ============================================================
// WATCH — recarrega quando qualquer ID mudar
// ============================================================
watch(
  () => [props.idEmbarque, props.idPurchaseOrder, props.idOrdemCompra],
  () => { carregarSolicitacoes() },
  { immediate: true }
)

// ============================================================
// HELPERS
// ============================================================
const obterCorStatus = (lstStatus) => {
  if (lstStatus === FIN_ST_SOL_ADT.Pendente) return 'badge-ghost-warning'
  if (lstStatus === FIN_ST_SOL_ADT.Aprovado) return 'badge-ghost-success'
  if (lstStatus === FIN_ST_SOL_ADT.Cancelado) return 'badge-ghost-danger'
  return 'badge-ghost-secondary'
}

// ============================================================
// SORT
// ============================================================
const handleSortChange = ({ field, order }) => {
  sortField.value = field || ''
  sortOrder.value = order || ''
  carregarSolicitacoes()
}

// ============================================================
// ESTADO DO MODAL
// ============================================================
const showModal = ref(false)
const modalModo = ref('criacao') // 'criacao' | 'edicao' | 'visualizacao'
const modalData = ref(null)

const abrirModal = (solicitacao = null) => {
  modalModo.value = solicitacao ? 'edicao' : 'criacao'
  modalData.value = solicitacao
  showModal.value = true
}

const abrirModalVisualizar = (solicitacao) => {
  modalModo.value = 'visualizacao'
  modalData.value = solicitacao
  showModal.value = true
}

const handleEditar = (row) => {
  abrirModal(row)
}

const handleVisualizar = (row) => {
  abrirModalVisualizar(row)
}

// ============================================================
// CONFIRMATION MODAL PARA CANCELAMENTO
// ============================================================
const confirmationModalRef = ref(null)
const solicitacaoParaCancelar = ref(null)

const handleCancelar = (row) => {
  solicitacaoParaCancelar.value = row
  confirmationModalRef.value?.show()
}

const onConfirmarCancelamento = async (confirmou) => {
  if (!confirmou || !solicitacaoParaCancelar.value) {
    solicitacaoParaCancelar.value = null
    return
  }

  const row = solicitacaoParaCancelar.value
  solicitacaoParaCancelar.value = null

  try {
    await api.put(`/SolicitacoesAdiantamento/${row.idSolicitacaoAdiantamento}`, {
      idParceiro: row.idParceiro,
      valorTotal: row.valorTotal,
      observacao: row.observacao,
      idEmbarque: row.idEmbarque,
      lstMoeda: row.lstMoeda,
      dataVencimento: row.dataVencimento,
      numeroFaturaOriginal: row.numeroFaturaOriginal,
      lstFormaPagamento: row.lstFormaPagamento,
      codigoBarrasOuPix: row.codigoBarrasOuPix,
      lstStatusSolicitacao: FIN_ST_SOL_ADT.Cancelado,
      itens: (row.itens || []).map(i => ({ descricao: i.descricao, valor: i.valor }))
    })
    emit('show-notification', { message: 'Solicitação cancelada com sucesso!', type: 'success' })
    emit('acao-completa', { tipoAcao: 'cancelar', solicitacao: row })
    await carregarSolicitacoes()
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao cancelar solicitação.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

const handleNovaSolicitacao = () => {
  abrirModal()
}

// ============================================================
// SALVAR VIA MODAL
// ============================================================
const handleSalvarModal = async (payload) => {
  try {
    if (payload.idSolicitacaoAdiantamento) {
      await api.put(`/SolicitacoesAdiantamento/${payload.idSolicitacaoAdiantamento}`, payload)
    } else {
      await api.post('/SolicitacoesAdiantamento', payload)
    }
    emit('show-notification', { message: 'Solicitação de adiantamento salva com sucesso!', type: 'success' })
    showModal.value = false
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao salvar solicitação de adiantamento.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

// ============================================================
// WATCH — recarrega quando o modal for fechado (único ponto de reload)
// ============================================================
watch(showModal, (novoValor, valorAntigo) => {
  if (!novoValor && valorAntigo) {
    carregarSolicitacoes()
  }
})

defineExpose({ carregarSolicitacoes, abrirModal })
</script>

<template>
  <div class="card mb-3 border-info-subtle">
    <div class="card-header bg-info-subtle d-flex justify-content-between align-items-center">
      <h5 class="mb-0 text-info-emphasis fw-bold">
        <i class="bi bi-cash-coin me-2"></i>Solicitações de Adiantamento
        <span
          v-if="solicitacoes.length > 0"
          class="ms-3 fw-normal fs-6 badge bg-info bg-opacity-10 text-info-emphasis border border-info-subtle"
        >
          {{ solicitacoes.length }}
          {{ solicitacoes.length === 1 ? 'solicitação' : 'solicitações' }}
        </span>
      </h5>
      <button
        v-if="props.podeEditar"
        type="button"
        class="btn btn-sm btn-info text-white"
        @click="handleNovaSolicitacao"
      >
        <i class="bi bi-plus-lg"></i> Nova Solicitação
      </button>
    </div>
    <div class="card-body p-0">
      <AdminTable
        :columns="colunas"
        :data="solicitacoes"
        :loading="isLoading"
        :sort-field="sortField"
        :sort-order="sortOrder"
        row-key="idSolicitacaoAdiantamento"
        :selectable="false"
        empty-message="Nenhuma solicitação de adiantamento encontrada."
        @sort-change="handleSortChange"
      >
        <!-- Status (nomeStatusSolicitacao já resolvido pelo backend) -->
        <template #status="{ row }">
          <span :class="['badge', obterCorStatus(row.lstStatusSolicitacao)]">
            {{ row.nomeStatusSolicitacao || row.lstStatusSolicitacao || '-' }}
          </span>
        </template>

        <!-- Ações -->
        <template #acoes="{ row }">
          <div class="d-flex gap-1">
            <button
              v-if="props.podeEditar && row.lstStatusSolicitacao === FIN_ST_SOL_ADT.Pendente"
              type="button"
              class="btn-icon"
              title="Editar solicitação"
              @click="handleEditar(row)"
            >
              <i class="bi bi-pencil-fill"></i>
            </button>
            <button
              v-if="props.podeVisualizar"
              type="button"
              class="btn-icon"
              title="Visualizar solicitação"
              @click="handleVisualizar(row)"
            >
              <i class="bi bi-eye-fill"></i>
            </button>
            <button
              v-if="props.podeEditar && row.lstStatusSolicitacao === FIN_ST_SOL_ADT.Pendente"
              type="button"
              class="btn-icon text-danger"
              title="Cancelar solicitação"
              @click="handleCancelar(row)"
            >
              <i class="bi bi-x-circle-fill"></i>
            </button>
          </div>
        </template>
      </AdminTable>
    </div>

    <!-- MODAL: Cadastro/Edição/Visualização -->
    <SolicitacaoAdiantamentoModal
      v-model="showModal"
      :modo="modalModo"
      :solicitacao="modalData"
      :id-embarque="props.idEmbarque"
      :id-organizacao="props.idOrganizacao || authStore.organizacaoAtiva"
      :listas="listasInternas"
      @salvar="handleSalvarModal"
      @show-notification="(p) => emit('show-notification', p)"
    />

    <!-- Confirmation Modal para cancelamento -->
    <ConfirmationModal
      ref="confirmationModalRef"
      title="Cancelar Solicitação"
      message="Deseja realmente cancelar esta solicitação de adiantamento?"
      confirm-button-text="Sim, cancelar"
      cancel-button-text="Não"
      @confirmed="onConfirmarCancelamento"
    />
  </div>
</template>

<style scoped>
.admin-table-container :deep(.admin-table) {
  width: 100%;
}

.btn-icon {
  background: none;
  border: none;
  color: #6c7d8e;
  padding: 0.4rem;
  border-radius: 50%;
  line-height: 1;
  transition: background-color 0.2s, color 0.2s;
}

.btn-icon:hover {
  background-color: #e2e6ea;
  color: #212934;
}

[data-bs-theme="dark"] .btn-icon:hover {
  background-color: #343a40;
  color: #fff;
}

.btn-icon.text-danger {
  color: #dc3545;
}

.btn-icon.text-danger:hover {
  background-color: rgba(220, 53, 69, 0.1);
  color: #b02a37;
}
</style>
